import { z } from 'zod';

import {
  RoleSchema,
  RecurrenceRuleSchema,
  AttendanceSchema,
  AudienceInputShape,
  ChurchStatusSchema,
  CivilStatusSchema,
  JoinedViaSchema,
  SexSchema,
  ScheduleResponseSchema,
  EventResourceSchema,
  MemberAccessSchema,
  HexColorSchema,
  IdSchema,
  IsoDateSchema,
  MemberSchema,
  MinistryIconKeySchema,
  RoleIconKeySchema,
  MinistrySchema,
  OrganizationSchema,
  OrgSlugSchema,
  PHOTO_BASE64_MAX,
  PhotoTypeSchema,
  RoleRulesShape,
  RoleSexSchema,
  SessionSchema,
  SettingsSchema,
  AccountPrivacySchema,
  ThemeSchema,
  TimeSchema,
} from './resources';

/** Corpos de requisição e respostas das mutações — um por ação da store do client. */

// ===== Autenticação =====

export const LoginRequestSchema = z
  .object({
    email: z.email(),
    password: z.string().min(1).max(200),
    org: OrgSlugSchema.optional().meta({
      description:
        'Endereço da organização (o passo "Encontre sua igreja"). Sem ele, vale a única organização em que o e-mail tem acesso.',
    }),
    remember: z
      .boolean()
      .default(true)
      .meta({ description: '"Manter conectado": sem ele, a sessão acaba ao fechar o navegador.' }),
  })
  .meta({ id: 'LoginRequest' });

export const AuthResponseSchema = z
  .object({
    accessToken: z.string(),
    expiresIn: z.number().int().meta({ description: 'Validade do access token, em segundos.' }),
    session: SessionSchema,
  })
  .meta({
    id: 'AuthResponse',
    description: 'O refresh token vai no cookie httpOnly `escala_refresh`, nunca no corpo.',
  });

export const MeSchema = z
  .object({
    session: SessionSchema,
    email: z.string(),
    name: z.string(),
    theme: ThemeSchema.nullable().meta({
      description: 'Tema escolhido pelo usuário; `null` enquanto ele não escolheu.',
    }),
    org: OrganizationSchema.meta({
      description: 'A organização ativa (a do acesso ou, para a plataforma, a escolhida em `X-Org-Id`).',
    }),
    privacy: AccountPrivacySchema,
  })
  .meta({ id: 'Me', description: 'Quem está autenticado.' });

export const PreferencesRequestSchema = z
  .object({ theme: ThemeSchema })
  .meta({ id: 'PreferencesRequest', description: 'Preferências do usuário, guardadas com o acesso.' });

/** Tamanho; os demais requisitos (`@escala/domain/password`) o servidor confere e explica. */
export const PasswordSchema = z.string().min(8, 'A senha precisa de pelo menos 8 caracteres').max(200);

export const InvitationPreviewSchema = z
  .object({
    status: z.enum(['valid', 'expired', 'used', 'invalid']),
    email: z.string(),
    firstName: z.string(),
    inviter: z.string().meta({ description: 'Nome de quem convidou.' }),
  })
  .meta({
    id: 'InvitationPreview',
    description:
      'O que a tela do convite mostra antes de criar a senha. Link inválido vem com os textos vazios.',
  });

export const AcceptInvitationRequestSchema = z
  .object({
    token: z.string().min(1),
    password: PasswordSchema,
    termsVersion: z.string().min(1).max(20).meta({
      description:
        'Versão dos Termos de Uso e da Política de Privacidade que a pessoa aceitou ao criar a senha (a atual; senão 409 `terms_outdated`).',
    }),
  })
  .meta({ id: 'AcceptInvitationRequest' });

export const PasswordResetRequestSchema = z
  .object({ email: z.email(), org: OrgSlugSchema.optional() })
  .meta({ id: 'PasswordResetRequest' });

export const PasswordResetConfirmSchema = z
  .object({ token: z.string().min(1), password: PasswordSchema })
  .meta({ id: 'PasswordResetConfirm' });

export const ChangePasswordRequestSchema = z
  .object({ currentPassword: z.string().min(1).max(200), newPassword: PasswordSchema })
  .meta({ id: 'ChangePasswordRequest', description: 'Encerra as sessões dos outros dispositivos.' });

// ===== Acessos =====

export const AccessListSchema = z.object({ items: z.array(MemberAccessSchema) }).meta({ id: 'AccessList' });

export const InviteRequestSchema = z.object({ memberIds: z.array(IdSchema).min(1).max(500) }).meta({
  id: 'InviteRequest',
  description: 'Envia (ou reenvia) o convite por e-mail. O convite anterior de cada um deixa de valer.',
});

export const InviteResponseSchema = z
  .object({
    sent: z.array(IdSchema),
    skipped: z.array(z.object({ memberId: IdSchema, reason: z.string() })),
    items: z.array(MemberAccessSchema).meta({ description: 'Os acessos depois do envio.' }),
  })
  .meta({ id: 'InviteResponse' });

export const AccessRolePatchSchema = z.object({ role: z.enum(['admin', 'member']) }).meta({
  id: 'AccessRolePatch',
  description:
    'Só Administrador ou Membro: quem lidera um ministério ou uma função passa a Líder sozinho (e volta a Membro ' +
    'quando deixa de liderar).',
});

// ===== Ministérios e funções =====

export const MinistryInputSchema = z
  .object({
    name: z.string().trim().min(1).max(80),
    description: z.string().max(500),
    color: MinistrySchema.shape.color,
    icon: MinistryIconKeySchema,
    leaderIds: MinistrySchema.shape.leaderIds,
    welcome: MinistrySchema.shape.welcome
      .optional()
      .meta({ description: 'Ministério de acolhimento (só o Administrador muda); ausente = mantém.' }),
  })
  .meta({ id: 'MinistryInput' });

/**
 * Id gerado no cliente (opcional) nos cadastros: a interface aplica a mudança na hora e
 * sincroniza depois, já sabendo o id. Repetido = 409.
 */
export const ClientIdSchema = z
  .string()
  .regex(/^[A-Za-z0-9_-]{8,64}$/, 'Id com 8 a 64 letras, números, - ou _')
  .optional();

/** Semente dos ids gerados em lote (série de eventos, importação). */
export const IdSeedSchema = z
  .string()
  .regex(/^[a-z0-9]{6,32}$/, 'Semente com 6 a 32 letras minúsculas e números')
  .optional();

/** Função que já nasce com o ministério (criado a partir de um modelo). */
export const RoleSeedSchema = z
  .object({
    id: ClientIdSchema,
    name: z.string().trim().min(1).max(80),
    icon: RoleIconKeySchema,
    qty: z.number().int().min(1).max(20),
    allowedSex: RoleSexSchema,
  })
  .meta({ id: 'RoleSeed' });

export const MinistryCreateSchema = MinistryInputSchema.extend({
  id: ClientIdSchema,
  roles: z.array(RoleSeedSchema).max(30).optional().meta({
    description: 'Funções que já nascem com o ministério ("Usar modelo"); sem nome repetido.',
  }),
}).meta({
  id: 'MinistryCreate',
});

export const RoleCreateSchema = z
  .object({
    id: ClientIdSchema,
    name: z.string().trim().min(1).max(80).optional(),
    icon: RoleIconKeySchema.optional(),
  })
  .meta({ id: 'RoleCreate', description: 'Sem nome, a função nasce como "Nova função".' });

export const RolePatchSchema = z
  .object({
    name: z.string().trim().min(1).max(80),
    qty: z.number().int().min(1).max(20),
    allowedSex: RoleSexSchema,
    rules: z.array(z.string().trim().min(1).max(120)).max(30),
    leaderIds: RoleSchema.shape.leaderIds,
    icon: RoleIconKeySchema,
    ...RoleRulesShape,
  })
  .partial()
  .meta({
    id: 'RolePatch',
    description:
      'Líder de Função altera só `rules`, `allowedSex` e as regras avançadas; nome, ícone, quantidade e líder ficam com ' +
      'quem edita o ministério. Em treinamento, só membros habilitados na função.',
  });

// ===== Membros =====

export const MemberInputSchema = MemberSchema.omit({ id: true })
  .extend({
    nickname: MemberSchema.shape.nickname.default('').meta({
      description: 'Nome de apresentação; ausente vale `""` (a interface de antes do campo não o manda).',
    }),
    inactiveSince: MemberSchema.shape.inactiveSince.optional().meta({
      description: 'Ignorado: a situação nas escalas muda só por `/deactivate` e `/reactivate`.',
    }),
    photo: MemberSchema.shape.photo.optional().meta({
      description: 'Ignorado: a foto muda só por `PUT`/`DELETE /members/{id}/photo`.',
    }),
    selfUpdatedAt: MemberSchema.shape.selfUpdatedAt.optional().meta({
      description: 'Ignorado: muda só quando a própria pessoa salva pelo link de completar.',
    }),
    admin: MemberSchema.shape.admin.optional().meta({
      description: 'Ignorado: o perfil de Administrador muda só em Acessos (`PATCH /accesses/{memberId}`).',
    }),
    location: MemberSchema.shape.location.optional().meta({
      description: 'Ignorado: as coordenadas vêm do endereço, buscadas pelo servidor.',
    }),
  })
  .meta({
    id: 'MemberInput',
    description: 'Cadastro inteiro do membro, gravado de uma vez (o "Salvar" do perfil).',
  });

export const MemberCreateSchema = MemberInputSchema.extend({ id: ClientIdSchema }).meta({
  id: 'MemberCreate',
});

export const MemberEditSchema = z
  .object({
    memberId: IdSchema,
    roles: MemberSchema.shape.roles.optional(),
    availability: MemberSchema.shape.availability.optional(),
    fill: MemberSchema.pick({
      nickname: true,
      birthDate: true,
      email: true,
      phone: true,
      civilStatus: true,
      profession: true,
      churchStatus: true,
      memberSince: true,
      joinedVia: true,
      baptismDate: true,
      originChurch: true,
      cpf: true,
      address: true,
      emergency: true,
    })
      .partial()
      .optional()
      .meta({
        description:
          'Campos vazios do cadastro que a planilha completa (só o Administrador; campo já preenchido é 409).',
      }),
  })
  .meta({
    id: 'MemberEdit',
    description:
      'O que muda num membro no lote: as funções habilitadas inteiras (ministryId → roleIds) e/ou a ' +
      'disponibilidade (21 períodos). A parte ausente fica como está.',
  });

export const MemberBulkEditRequestSchema = z
  .object({ edits: z.array(MemberEditSchema).min(1).max(1000) })
  .meta({
    id: 'MemberBulkEditRequest',
    description:
      'Edição de vários membros de uma vez (o modo Editor de Membros e a etapa "Atribuir" da importação), ' +
      'gravada numa transação só, com as regras do "Salvar" do perfil.',
  });

export const PhotoUploadSchema = z
  .object({
    type: PhotoTypeSchema,
    data: z
      .string()
      .min(1)
      .max(PHOTO_BASE64_MAX)
      .regex(/^[A-Za-z0-9+/]+={0,2}$/, 'Base64 inválido.')
      .meta({ description: 'A imagem em base64 (sem o prefixo `data:`).' }),
  })
  .meta({
    id: 'PhotoUpload',
    description:
      'A foto já recortada e reduzida pela interface (quadrado de 256 px, até 64 KB). O servidor confere o tipo ' +
      'pelos bytes do arquivo, o tamanho e as dimensões (até 1024 px).',
  });

export const ImportRequestSchema = z
  .object({ csv: z.string().min(1).max(500_000), idSeed: IdSeedSchema })
  .meta({
    id: 'ImportRequest',
    description:
      'Planilha CSV (`;`, `,` ou tabulação) com uma coluna por campo do cadastro — os títulos são os rótulos do ' +
      'perfil; só "Nome" e "Sexo" são obrigatórios. Funções, ministérios, cônjuge e foto ficam de fora.',
  });

export const ImportFieldSchema = z
  .enum([
    'name',
    'nickname',
    'sex',
    'birthDate',
    'email',
    'phone',
    'civilStatus',
    'profession',
    'churchStatus',
    'memberSince',
    'joinedVia',
    'baptismDate',
    'originChurch',
    'cpf',
    'address.cep',
    'address.street',
    'address.number',
    'address.complement',
    'address.district',
    'address.city',
    'address.uf',
    'emergency.name',
    'emergency.relation',
    'emergency.phone',
  ])
  .meta({ id: 'ImportField', description: 'Campo do cadastro que a coluna preenche.' });

const ImportIssueSchema = z
  .object({ field: ImportFieldSchema.nullable(), message: z.string() })
  .meta({ id: 'ImportIssue', description: '`field` nulo = a linha toda.' });

/** Como a linha foi lida — sem os limites do cadastro, porque a linha recusada também volta. */
const ImportedMemberSchema = z
  .object({
    name: z.string(),
    nickname: z.string(),
    sex: SexSchema.nullable(),
    birthDate: IsoDateSchema.nullable(),
    email: z.string(),
    phone: z.string(),
    civilStatus: CivilStatusSchema.nullable(),
    profession: z.string(),
    churchStatus: ChurchStatusSchema.nullable(),
    memberSince: IsoDateSchema.nullable(),
    joinedVia: JoinedViaSchema.nullable(),
    baptismDate: IsoDateSchema.nullable(),
    originChurch: z.string(),
    cpf: z.string(),
    address: z.object({
      cep: z.string(),
      street: z.string(),
      number: z.string(),
      complement: z.string(),
      district: z.string(),
      city: z.string(),
      uf: z.string(),
    }),
    emergency: z.object({ name: z.string(), relation: z.string(), phone: z.string() }),
  })
  .meta({ id: 'ImportedMember' });

export const MemberMatchSchema = z
  .object({
    memberId: IdSchema,
    score: z.number().min(0).max(1),
    level: z.enum(['certain', 'likely', 'possible']),
    reasons: z.array(z.string()),
  })
  .meta({
    id: 'MemberMatch',
    description:
      'Um cadastro que parece a pessoa da linha: `certain` (mesmo nome, e-mail, CPF ou telefone), `likely` ou `possible`.',
  });

export const ImportRowSchema = z
  .object({
    line: z.number().int(),
    raw: z
      .partialRecord(ImportFieldSchema, z.string())
      .meta({ description: 'O texto de cada coluna, como veio.' }),
    member: ImportedMemberSchema,
    errors: z.array(ImportIssueSchema).meta({ description: 'Tiram a linha da importação.' }),
    warnings: z
      .array(ImportIssueSchema)
      .meta({ description: 'O valor é descartado e a linha entra assim mesmo.' }),
    ok: z.boolean(),
    matches: z.array(MemberMatchSchema).meta({ description: 'Os cadastros que parecem esta pessoa.' }),
    linkTo: IdSchema.nullable().meta({
      description: 'A linha é de quem já está cadastrado: não cria ninguém, só completa o cadastro.',
    }),
    suggestedSex: SexSchema.nullable().meta({
      description: 'Sem sexo na planilha: o palpite pelo primeiro nome.',
    }),
  })
  .meta({ id: 'ImportRow' });

export const ImportResponseSchema = z
  .object({
    imported: z.number().int(),
    rows: z.array(ImportRowSchema),
    error: z.string().meta({ description: 'Problema da planilha inteira (`""` quando foi lida).' }),
    columns: z.array(ImportFieldSchema).meta({ description: 'Colunas reconhecidas, na ordem do modelo.' }),
    ignoredColumns: z.array(z.string()).meta({ description: 'Títulos que não viraram campo, com o motivo.' }),
  })
  .meta({ id: 'ImportResponse' });

// ===== Eventos =====

const EventFieldsSchema = z.object({
  title: z.string().trim().min(1).max(120),
  color: HexColorSchema.nullable()
    .optional()
    .meta({
      description:
        'A cor do evento (`#RRGGBB`) ou `null` para a automática da série. Na edição, vale em todas as datas do ' +
        'alcance (não mexe na escala); ausente, cada data mantém a sua.',
    }),
  date: IsoDateSchema,
  time: TimeSchema,
  ministryIds: z.array(IdSchema),
  need: z.record(IdSchema, z.number().int().min(0).max(50)),
});

export const EventCreateSchema = EventFieldsSchema.extend({
  recurrence: RecurrenceRuleSchema.nullable().meta({
    description: 'A repetição (`null` = não se repete). A `start` vale a `date` do evento.',
  }),
  idSeed: IdSeedSchema,
}).meta({ id: 'EventCreate', description: 'Evento único ou série semanal (12 ocorrências).' });

export const EventScopeSchema = z.enum(['one', 'following', 'series']).meta({
  id: 'EventScope',
  description: 'Na série: só esta data, esta e as próximas (a partir desta) ou a série toda.',
});

export const EventUpdateSchema = EventFieldsSchema.extend({
  scope: EventScopeSchema,
  until: IsoDateSchema.nullable()
    .optional()
    .meta({
      description:
        'Na série (`following` ou `series`): a nova data de término. Estende pela regra (até 104 datas novas) ou ' +
        'tira as datas depois dela — nunca antes de hoje. Sem ela, o término fica como está.',
    }),
  includeScheduled: z
    .boolean()
    .default(true)
    .meta({ description: 'Na série: aplica também às datas futuras que já têm escala.' }),
  includePastEmpty: z
    .boolean()
    .default(true)
    .meta({ description: 'Na série: aplica também às datas passadas ainda sem registro.' }),
}).meta({
  id: 'EventUpdate',
  description:
    'Edita só esta data ou a série. Remover um ministério ou zerar uma função tira quem estava escalado nas datas ' +
    'afetadas. As datas que já aconteceram com quem serviu marcado nunca perdem ninguém: na série recebem só o ' +
    'título; editadas sozinhas, recusam (409 `history_protected`) o que tiraria alguém, e o horário não muda.',
});

// No `?scope=` o enum vai sem `id`: parâmetro de consulta não aceita `$ref` na OpenAPI.
export const EventScopeQuerySchema = z.object({
  scope: z.enum(['one', 'following', 'series']).default('one'),
});

export const EventsQuerySchema = z.object({
  from: IsoDateSchema.optional(),
  to: IsoDateSchema.optional(),
});

export const EventsCreatedSchema = z
  .object({ events: z.array(EventResourceSchema) })
  .meta({ id: 'EventsCreated' });

export const BackfillRequestSchema = z
  .object({
    since: IsoDateSchema.meta({ description: 'Desde quando o evento acontece ("Desde").' }),
  })
  .meta({
    id: 'BackfillRequest',
    description:
      'Ocorrências passadas: as datas semanais desde `since` até a véspera da primeira já existente, sem ' +
      'duplicar, com os ministérios e funções da série. No máximo 160 por vez (as mais recentes).',
  });

export const OccurrenceRequestSchema = z
  .object({
    date: IsoDateSchema.meta({ description: 'A nova data do evento.' }),
    time: TimeSchema,
  })
  .meta({
    id: 'OccurrenceRequest',
    description:
      'Nova data de um evento único que já existe (o que se repete sem data fixa): entra no mesmo evento, com ' +
      'o título, os ministérios e as quantidades da última data; a escala começa vazia.',
  });

export const SkipOccurrenceRequestSchema = z
  .object({
    reason: z.string().max(80).meta({ description: 'O motivo, opcional ("Feriado").' }),
  })
  .meta({
    id: 'SkipOccurrenceRequest',
    description:
      'Exclui só esta data de uma série que se repete: a série continua, a data fica guardada na regra ' +
      '(não volta ao estender nem no passado) e quem estava na escala publicada é avisado.',
  });

export const RestoreSkippedRequestSchema = z
  .object({ date: IsoDateSchema.meta({ description: 'A data excluída que volta à série.' }) })
  .meta({
    id: 'RestoreSkippedRequest',
    description: 'Traz de volta uma data excluída da série, com a configuração da série e a escala vazia.',
  });

// ===== Histórico (quem serviu nos eventos passados) =====

export const ServedRequestSchema = z
  .object({ roleId: IdSchema, memberId: IdSchema, served: z.boolean() })
  .meta({
    id: 'ServedRequest',
    description: 'Marca (ou desmarca) quem serviu na função de um evento que já aconteceu.',
  });

export const HistoryRolesRequestSchema = z
  .object({
    roleIds: z
      .array(IdSchema)
      .optional()
      .meta({ description: 'As funções do evento (padrão: todas as que quem envia edita).' }),
  })
  .meta({ id: 'HistoryRolesRequest' });

export const RecordedRequestSchema = z
  .object({ recorded: z.boolean() })
  .meta({ id: 'RecordedRequest', description: '"Concluir" (true) ou "Reabrir" (false) o registro da data.' });

// ===== Escala =====

export const AssignRequestSchema = z
  .object({ roleId: IdSchema, memberId: IdSchema, replaceId: IdSchema.nullable().optional() })
  .meta({ id: 'AssignRequest', description: 'Escala alguém (ou substitui `replaceId`) numa função.' });

export const MoveRequestSchema = z
  .object({ fromRoleId: IdSchema, toRoleId: IdSchema, memberId: IdSchema })
  .meta({ id: 'MoveRequest' });

export const SlotsRestoreSchema = z
  .object({
    slots: z.record(IdSchema, z.array(IdSchema)),
    published: z.boolean(),
    generated: z.boolean(),
    responses: z
      .record(IdSchema, ScheduleResponseSchema)
      .optional()
      .meta({
        description:
          'As recusas do momento restaurado: preencher a vaga reaberta consome a recusa, e desfazer a traz de volta. ' +
          'Só valem as das funções que o perfil edita; confirmações são ignoradas.',
      }),
  })
  .meta({ id: 'SlotsRestore', description: 'Restaura a escala de um evento — é o "Desfazer".' });

export const EventConflictSchema = z
  .object({
    memberId: IdSchema,
    roleId: IdSchema,
    kind: z.enum(['blackout', 'unavailable', 'sex', 'duplicate']),
    message: z.string(),
  })
  .meta({ id: 'EventConflict' });

export const ScheduleStatusSchema = z
  .enum(['published', 'complete', 'partial', 'conflict', 'awaiting'])
  .meta({ id: 'ScheduleStatus' });

export const ScheduleResultSchema = z
  .object({
    event: EventResourceSchema,
    status: ScheduleStatusSchema,
    filled: z.number().int(),
    needed: z.number().int(),
    conflicts: z.array(EventConflictSchema),
  })
  .meta({
    id: 'ScheduleResult',
    description:
      'O evento depois da edição, com a situação derivada. Regra violada não bloqueia: vira conflito.',
  });

export const CandidateSchema = z
  .object({
    memberId: IdSchema,
    tag: z.enum(['available', 'unset', 'scheduled', 'sex', 'unavailable', 'blackout']),
    issue: z.object({ title: z.string(), detail: z.string() }).nullable(),
    rules: z
      .array(
        z.object({
          kind: z.enum(['pair', 'couple', 'trainee', 'experience', 'years']),
          title: z.string(),
          detail: z.string(),
        })
      )
      .meta({
        description:
          'Regras avançadas da função e o revezamento do casal que a escolha não cumpre (avisam, não bloqueiam).',
      }),
    trainee: z.boolean(),
    experience: z.number().int().meta({ description: 'Escalas anteriores nesta função.' }),
    partnerId: IdSchema.nullable().meta({
      description: 'O parceiro de "servem juntos" que já está nesta função do evento (vem antes na lista).',
    }),
    monthCount: z.number().int(),
    suggested: z.boolean(),
  })
  .meta({ id: 'Candidate' });

export const MoveTargetSchema = z
  .object({ roleId: IdSchema, label: z.string(), enabled: z.boolean(), reason: z.string() })
  .meta({ id: 'MoveTarget' });

// ===== Confirmação de presença, check-in e cobrança =====

export const RespondRequestSchema = z
  .object({
    status: z.enum(['confirmed', 'declined']),
    reason: z
      .string()
      .trim()
      .max(200)
      .optional()
      .meta({ description: 'Motivo de "Não posso ir" (opcional).' }),
  })
  .meta({
    id: 'RespondRequest',
    description:
      'Resposta do próprio membro à escala publicada. "Não posso ir" tira a pessoa e reabre a vaga.',
  });

export const UndoResponseRequestSchema = z
  .object({
    restore: z
      .enum(['pending', 'confirmed'])
      .meta({ description: 'Como estava antes da resposta desfeita: pendente ou confirmado.' }),
  })
  .meta({
    id: 'UndoResponseRequest',
    description:
      'O "Desfazer" do aviso, logo depois de responder. Desfazer "Não posso ir" volta a pessoa à função, se a ' +
      'vaga ainda não foi preenchida.',
  });

export const AttendanceRequestSchema = z
  .object({
    marks: z
      .record(IdSchema, AttendanceSchema)
      .meta({ description: 'memberId → marcação. Quem fica de fora das funções do líder é ignorado.' }),
  })
  .meta({
    id: 'AttendanceRequest',
    description: 'Check-in do dia: substitui as marcações das funções que quem envia edita.',
  });

export const AudienceRequestSchema = z.object(AudienceInputShape).meta({
  id: 'AudienceRequest',
  description:
    'Registrar ou editar o público do culto. O servidor confere com `checkAudience` do domínio (crianças dentro do ' +
    'total); membros + visitantes acima do total são aceitos — a interface pede confirmação antes.',
});

export const NudgeResultSchema = z
  .object({
    sent: z.number().int().meta({ description: 'Pendentes que receberam a cobrança.' }),
    event: EventResourceSchema,
  })
  .meta({ id: 'NudgeResult', description: 'Cobrança enviada aos pendentes da área de quem cobrou.' });

export const WhatsAppRequestSchema = z
  .object({ memberId: IdSchema.meta({ description: 'O pendente que recebeu a mensagem.' }) })
  .meta({
    id: 'WhatsAppRequest',
    description:
      'Registra no histórico do evento que o líder abriu o WhatsApp para cobrar um pendente. A mensagem sai do ' +
      'aparelho de quem envia; o sistema não confirma o envio.',
  });

// ===== Pedido de troca =====

export const SwapCreateRequestSchema = z
  .object({
    id: ClientIdSchema,
    toId: IdSchema.meta({ description: 'O colega habilitado na mesma função, fora do evento.' }),
  })
  .meta({
    id: 'SwapCreateRequest',
    description: '"Pedir troca": o próprio escalado pede ao colega que assuma a sua função.',
  });

export const SwapAnswerRequestSchema = z
  .object({ accept: z.boolean().meta({ description: 'O colega aceita (vai ao líder) ou recusa.' }) })
  .meta({ id: 'SwapAnswerRequest' });

export const SwapDecisionRequestSchema = z
  .object({ approve: z.boolean().meta({ description: 'O líder aprova (a escala muda) ou recusa.' }) })
  .meta({ id: 'SwapDecisionRequest' });

export const SwapCandidateSchema = z
  .object({
    memberId: IdSchema,
    state: z.enum(['available', 'unset', 'unavailable', 'blackout']).meta({
      description: 'Disponível, sem disponibilidade configurada, indisponível no horário ou ausente na data.',
    }),
    blocked: z.boolean().meta({ description: 'Indisponível ou ausente: não pode ser escolhido.' }),
  })
  .meta({
    id: 'SwapCandidate',
    description:
      'Colega para a troca, na ordem em que aparece (quem pode e serviu menos no mês primeiro). Só a situação na ' +
      'data — a disponibilidade, as ausências e quantas vezes o colega serviu não saem do servidor.',
  });

export const SwapCandidatesSchema = z.array(SwapCandidateSchema).meta({ id: 'SwapCandidates' });

export const UnavailableDateParamsSchema = z.object({ date: IsoDateSchema });

// ===== Agenda pessoal =====

export const CalendarFeedSchema = z
  .object({
    url: z
      .url()
      .meta({ description: 'Endereço secreto do calendário (.ics), para colar em qualquer agenda.' }),
    webcal: z.string().meta({ description: 'O mesmo com `webcal://` (abre direto na agenda do celular).' }),
    google: z.url().meta({ description: 'Abre o Google Agenda para adicionar a assinatura.' }),
    outlook: z.url(),
  })
  .meta({
    id: 'CalendarFeed',
    description: 'Assinatura das escalas do membro. Quem tem o link vê as escalas: gere outro se vazar.',
  });

// ===== Configurações, avisos, onboarding =====

export const SettingsPatchSchema = SettingsSchema.partial().meta({ id: 'SettingsPatch' });

export const OnboardingRequestSchema = z.object({ hidden: z.boolean() }).meta({ id: 'OnboardingRequest' });

export const MarkReadRequestSchema = z
  .object({ ids: z.array(z.string().min(1).max(200)).max(500) })
  .meta({ id: 'MarkReadRequest' });

export const NotificationSchema = z
  .object({
    id: z.string(),
    category: z.enum([
      'Escala',
      'Conflito',
      'Disponibilidade',
      'Ausência',
      'Lembrete',
      'Cadastro',
      'Privacidade',
      'Troca',
      'Visitante',
      'Aniversário',
    ]),
    tone: z.enum(['primary', 'danger', 'warning', 'success']),
    icon: z.enum(['send', 'alert', 'user', 'plane', 'calendar', 'swap', 'cake']),
    title: z.string(),
    text: z.string(),
    at: z.number().int(),
    target: z
      .discriminatedUnion('type', [
        z.object({ type: z.literal('event'), id: IdSchema }),
        z.object({ type: z.literal('member'), id: IdSchema }),
        z.object({ type: z.literal('registrations') }),
        z.object({ type: z.literal('agenda') }),
        z.object({ type: z.literal('privacy'), scope: z.enum(['answer', 'own']) }),
        z.object({ type: z.literal('visitors'), overdue: z.boolean() }),
        z.object({ type: z.literal('birthdays') }),
      ])
      .nullable(),
    read: z.boolean(),
  })
  .meta({ id: 'Notification', description: 'Derivada do estado: some quando a causa é resolvida.' });

export const NotificationsResponseSchema = z
  .object({ items: z.array(NotificationSchema), unread: z.number().int() })
  .meta({ id: 'NotificationsResponse' });

export type LoginRequest = z.infer<typeof LoginRequestSchema>;
export type AuthResponse = z.infer<typeof AuthResponseSchema>;
export type Me = z.infer<typeof MeSchema>;
export type PreferencesRequest = z.infer<typeof PreferencesRequestSchema>;
export type InvitationPreview = z.infer<typeof InvitationPreviewSchema>;
export type InviteResponse = z.infer<typeof InviteResponseSchema>;
export type MinistryInput = z.infer<typeof MinistryInputSchema>;
export type MinistryCreate = z.infer<typeof MinistryCreateSchema>;
export type MemberCreate = z.infer<typeof MemberCreateSchema>;
export type RolePatch = z.infer<typeof RolePatchSchema>;
export type MemberInput = z.infer<typeof MemberInputSchema>;
export type PhotoUpload = z.infer<typeof PhotoUploadSchema>;
export type MemberBulkEditRequest = z.infer<typeof MemberBulkEditRequestSchema>;
export type EventCreate = z.infer<typeof EventCreateSchema>;
export type EventUpdate = z.infer<typeof EventUpdateSchema>;
export type AssignRequest = z.infer<typeof AssignRequestSchema>;
export type MoveRequest = z.infer<typeof MoveRequestSchema>;
export type SlotsRestore = z.infer<typeof SlotsRestoreSchema>;
export type ScheduleResult = z.infer<typeof ScheduleResultSchema>;
export type SettingsPatch = z.infer<typeof SettingsPatchSchema>;
export type RespondRequest = z.infer<typeof RespondRequestSchema>;
export type UndoResponseRequest = z.infer<typeof UndoResponseRequestSchema>;
export type AttendanceRequest = z.infer<typeof AttendanceRequestSchema>;
export type AudienceRequest = z.infer<typeof AudienceRequestSchema>;
export type BackfillRequest = z.infer<typeof BackfillRequestSchema>;
export type OccurrenceRequest = z.infer<typeof OccurrenceRequestSchema>;
export type SkipOccurrenceRequest = z.infer<typeof SkipOccurrenceRequestSchema>;
export type RestoreSkippedRequest = z.infer<typeof RestoreSkippedRequestSchema>;
export type ServedRequest = z.infer<typeof ServedRequestSchema>;
export type HistoryRolesRequest = z.infer<typeof HistoryRolesRequestSchema>;
export type RecordedRequest = z.infer<typeof RecordedRequestSchema>;
export type NudgeResult = z.infer<typeof NudgeResultSchema>;
export type SwapCreateRequest = z.infer<typeof SwapCreateRequestSchema>;
export type SwapAnswerRequest = z.infer<typeof SwapAnswerRequestSchema>;
export type SwapDecisionRequest = z.infer<typeof SwapDecisionRequestSchema>;
export type SwapCandidateResource = z.infer<typeof SwapCandidateSchema>;
export type WhatsAppRequest = z.infer<typeof WhatsAppRequestSchema>;
export type CalendarFeed = z.infer<typeof CalendarFeedSchema>;

// ===== Distribuição =====

export const DistributionQuerySchema = z.object({
  from: IsoDateSchema.optional().meta({ description: 'Início do período (padrão: 90 dias até hoje).' }),
  to: IsoDateSchema.optional().meta({ description: 'Fim do período (padrão: hoje).' }),
  ministryId: IdSchema.optional().meta({
    description: 'Só o Administrador escolhe; os líderes veem o que lideram.',
  }),
});

export const DistributionSchema = z
  .object({
    from: IsoDateSchema.meta({ description: 'Início do período, já ajustado ao histórico de escalas.' }),
    to: IsoDateSchema,
    clipped: z.boolean().meta({ description: 'O período pedido começava antes do histórico.' }),
    historyStart: IsoDateSchema.nullable().meta({ description: 'Data do evento mais antigo.' }),
    monthly: z.boolean().meta({ description: 'Colunas por mês (períodos acima de 16 semanas).' }),
    buckets: z.array(
      z.object({
        start: IsoDateSchema,
        end: IsoDateSchema,
        month: z.string(),
        short: z.string(),
        label: z.string(),
        isCurrent: z.boolean(),
        generated: z.boolean(),
      })
    ),
    rows: z.array(
      z.object({
        memberId: IdSchema,
        cells: z.array(
          z.object({ count: z.number().int(), blackout: z.boolean(), items: z.array(z.string()) })
        ),
        total: z.number().int(),
        status: z.enum(['overload', 'balanced', 'low', 'none']),
        why: z.string(),
      })
    ),
    totals: z.array(z.number().int().nullable()),
    average: z.number(),
    served: z.number().int(),
    activeMembers: z.number().int(),
  })
  .meta({
    id: 'Distribution',
    description: 'Serviço nos ministérios: carga de escalas por membro no período.',
  });

export type DistributionQuery = z.infer<typeof DistributionQuerySchema>;
export type DistributionDto = z.infer<typeof DistributionSchema>;

// ===== Relatórios da Membresia =====

export const ReportKindSchema = z.enum(['culto', 'cresc', 'visit', 'perfil', 'serv', 'geo']);

export const ReportQuerySchema = z.object({
  from: IsoDateSchema.optional().meta({ description: 'Início do período (padrão: 90 dias até hoje).' }),
  to: IsoDateSchema.optional().meta({ description: 'Fim do período (padrão: hoje).' }),
  compare: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional()
    .meta({
      description: 'Compara com o período anterior equivalente (só o relatório de culto compara).',
    }),
});

export const ReportSchema = z
  .object({
    kind: ReportKindSchema,
    title: z.string(),
    summary: z.string().meta({ description: 'Uma frase com o resultado, a que "Copiar resumo" leva.' }),
    barsTitle: z.string(),
    bars: z.array(
      z.object({
        label: z.string(),
        text: z.string(),
        ratio: z.number().min(0).max(1),
        previous: z.number().min(0).max(1).nullable(),
        series: z.string().nullable(),
        note: z.string(),
      })
    ),
    tables: z.array(
      z.object({
        title: z.string(),
        head: z.array(z.string()),
        rows: z.array(
          z.object({
            cells: z.array(z.object({ text: z.string(), delta: z.number().nullable() })),
            total: z.boolean(),
          })
        ),
      })
    ),
    notes: z.array(z.string()),
    compared: z.boolean(),
  })
  .meta({
    id: 'Report',
    description: 'Relatório agregado da Membresia: tabelas prontas, sem nome, contato nem endereço.',
  });

export type ReportQuery = z.infer<typeof ReportQuerySchema>;
export type ReportDto = z.infer<typeof ReportSchema>;

// ===== Dados da igreja (Configurações) =====

export const ChurchDemoResponseSchema = z
  .object({
    members: z.number().int(),
    ministries: z.number().int(),
    events: z.number().int(),
  })
  .meta({
    id: 'ChurchDemoResponse',
    description: 'O que a demonstração criou: pessoas (com os administradores), ministérios e eventos.',
  });

export type ChurchDemoResponse = z.infer<typeof ChurchDemoResponseSchema>;

export const ClearChurchDataResponseSchema = z
  .object({ members: z.number().int() })
  .meta({ id: 'ClearChurchDataResponse', description: 'Quantos cadastros ficaram (os administradores).' });

export type ClearChurchDataResponse = z.infer<typeof ClearChurchDataResponseSchema>;
